/**
 * Shared B2B cart utilities.
 * These functions are used by both the mock layer and production code.
 * They should NOT be removed when the mock is cleaned up.
 */
import { VARIANT_TITLE_DELIMITER } from './cart.service.constants'

/**
 * Trims an API resource identifier and prevents empty IDs from being sent.
 * @example normalizeId(" grp_1 ", "groupId") // "grp_1"
 */
export const normalizeId = (id: string, idName: 'lineItemId' | 'groupId'): string => {
  const normalized = id.trim()
  if (!normalized) {
    throw new Error(`${idName} is required`)
  }
  return normalized
}

/**
 * Extracts color from a variant title string.
 * Assumes "Size / Color" format (e.g. "L/XL / Navy" -> "Navy").
 * Uses a two-pass strategy: first splits on space-padded slashes (` / `)
 * to handle compound sizes like "S/M", then falls back to bare slash.
 * Single-segment titles (e.g. "Navy") are treated as color with no size.
 *
 * @example extractColorFromVariantTitle("L/XL / Navy") // "Navy"
 * @example extractColorFromVariantTitle("Navy")        // "Navy"
 * @example extractColorFromVariantTitle("S/M / Red")   // "Red"
 * @example extractColorFromVariantTitle(undefined)      // ""
 */
export function extractColorFromVariantTitle(variantTitle?: string): string {
  if (!variantTitle) return ''

  // First pass: split on space-padded slashes (" / ") to preserve compound sizes like "S/M"
  const spacePaddedParts = variantTitle
    .split(' / ')
    .map(part => part.trim())
    .filter(Boolean)

  if (spacePaddedParts.length >= 2) {
    return spacePaddedParts[spacePaddedParts.length - 1] ?? ''
  }

  // Fallback: split on any slash using the original delimiter.
  // NOTE: bare compound sizes like "S/M" (no color) will return "M" as the color.
  // This is acceptable because compound sizes always come with a color via " / " in practice.
  const parts = variantTitle
    .split(VARIANT_TITLE_DELIMITER)
    .map(part => part.trim())
    .filter(Boolean)

  if (parts.length === 0) return ''
  if (parts.length === 1) return parts[0] ?? ''
  return parts[parts.length - 1] ?? ''
}
